import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Size } from '../../../../core/enums/Size';
import { BookingStatus } from '../../../../core/enums/BookingStatus';
import deliveryZoneColorMap from '../../../../core/constants/deliveryZoneColorMap';
import { formatBookingDuration, getDurationInMinutes } from '../../../../core/utils/date';

import { RootState } from '../../../../core/state/state';
import { Actions as BookingActions } from '../../../../core/state/bookings/actions';
import { selectDeliveryZoneById } from '../../../../core/state/delivery-zones/selectors';

import { BookingColumnLayout } from '../../types';

import InteractiveElement from '../../../../components/interactive-element/InteractiveElement';
import DeliveryZoneBadge from '../../../components/delivery-zone-badge/DeliveryZoneBadge';
import PendingBookingCountdown from '../../../components/pending-booking-countdown/PendingBookingCountdown';
import { Color } from '../../../../core/enums/Color';

import './BookingCard.scss';

interface Props {
  bookingLayout: BookingColumnLayout;
  isPrinting: boolean | undefined;
}

const BookingCard: React.FC<Props> = ({
  bookingLayout: { booking, rowStart, rowEnd, columnStart, columnEnd, expanded, halfHeight, halfWidth, concurrentBookingIds, hasRightWiseBooking },
  isPrinting
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedBooking = useSelector((state: RootState) => state.bookings.selectedBooking);
  const deliveryZone = useSelector((state: RootState) => selectDeliveryZoneById(state, booking.deliveryZoneId));

  const timeString = useMemo(() => formatBookingDuration(booking), [booking]);
  const isAnonymous = booking.isOwner === false;
  const isPending = !isAnonymous && booking.confirmationStatus === BookingStatus.Pending;
  const isDone = !isAnonymous && booking.confirmationStatus === BookingStatus.Done;
  const isBlocked = !isAnonymous && booking.confirmationStatus === BookingStatus.Blocked;
  const isTerminMoreThenOneHalfHour = getDurationInMinutes(booking.endTime, booking.startTime) >= 90;
  const isOnlyClockVisible = halfWidth || concurrentBookingIds.size > 1;
  const isCompactExpandedLayout = expanded && !halfHeight && !halfWidth && Boolean(hasRightWiseBooking);

  const getBookingCardDescriptionLines = () => {
    const hours = getDurationInMinutes(booking.endTime, booking.startTime) / 60;
    if (hours > 1) {
      return Math.floor(4 * (hours - 1));
    }
    return 1;
  };

  const openBooking = useCallback(() => {
    if (!isAnonymous) {
      dispatch(BookingActions.openDetails(booking));
    }
  }, [isAnonymous, dispatch, booking]);

  let className = `booking-card booking-card--color-${isBlocked ? Color.MiddleGrey : deliveryZoneColorMap[deliveryZone.creationIndex]}`;
  if (isDone) className += ` booking-card--done`;
  if (isBlocked) className += ` booking-card--blocked`;
  if (isPending) className += ` booking-card--pending`;
  if (expanded) className += ' booking-card--expanded';
  if (isCompactExpandedLayout) className += ' booking-card--compact-expanded';
  if (halfHeight) className += ' booking-card--half-height';
  if (booking.id === selectedBooking?.id) className += ' booking-card--details-open';

  const style = {
    gridArea: `${rowStart} / ${columnStart} / ${rowEnd} / ${isPrinting && columnEnd === 11 ? columnEnd + 1 : columnEnd}`
  };
  const content = (
    <>
      {expanded && (
        <>
          <div className="d-print-block d-none">
            <div className="booking-card__row">
              <DeliveryZoneBadge isBlocked={isBlocked} id={booking.deliveryZoneId} size={Size.Small} iconOnly={halfHeight} />
              <div className="d-flex flex-column">
                <span className={`booking-card__title booking-card__title--${halfWidth ? 'print-small' : 'print'}  text-truncate`}>
                  {!isAnonymous && booking.title}
                </span>
                <span className="booking-card__time booking-card__time--print">{timeString}</span>
              </div>

              {!isAnonymous && <div className="booking-card__status" />}
            </div>
          </div>
          {isCompactExpandedLayout ? (
            <div className="d-print-none">
              <DeliveryZoneBadge isBlocked={isBlocked} labelSize={Size.ExtraSmall} id={booking.deliveryZoneId} size={Size.Small} iconOnly />
              {!isAnonymous && <div className="booking-card__status" />}
            </div>
          ) : (
            <div className="booking-card__row d-print-none">
              <DeliveryZoneBadge
                isBlocked={isBlocked}
                labelSize={halfWidth ? Size.ExtraSmall : Size.Small}
                id={booking.deliveryZoneId}
                size={Size.Small}
                iconOnly={halfHeight}
              />
              {halfHeight && <p className="booking-card__title">{!isAnonymous && booking.title}</p>}
              {!isAnonymous && <div className="booking-card__status" />}
            </div>
          )}
        </>
      )}

      {!expanded && (
        <div className="d-flex align-items-center flex-column">
          <DeliveryZoneBadge isBlocked={isBlocked} labelSize={Size.ExtraSmall} id={booking.deliveryZoneId} size={Size.Small} iconOnly />
          {!isAnonymous && <div className="booking-card__status" />}
        </div>
      )}
      {expanded && !halfHeight && !isCompactExpandedLayout && (
        <>
          <p className="booking-card__title d-print-none">{!isAnonymous && booking.title}</p>
          <div className="booking-card__row">
            <p className="booking-card__time d-print-none">{timeString}</p>
            {isPending && (
              <div className="booking-card__countdown">
                <PendingBookingCountdown expanded={!isOnlyClockVisible} booking={booking} size={Size.Small} isShort={isOnlyClockVisible} />
              </div>
            )}
          </div>
        </>
      )}

      {expanded && isTerminMoreThenOneHalfHour && !isCompactExpandedLayout && (
        <div title={booking.description} style={{ WebkitLineClamp: getBookingCardDescriptionLines() }} className="booking-card__description">
          {booking.description}
        </div>
      )}
      {isPending && (!expanded || halfHeight) && concurrentBookingIds.size > 1 && (
        <div
          className={`booking-card__countdown booking-card__countdown--small ${
            !halfHeight && !halfWidth && !expanded ? 'd-flex w-100 justify-content-center' : ''
          }`}
        >
          <PendingBookingCountdown booking={booking} size={Size.Small} isShort={isOnlyClockVisible} />
        </div>
      )}
    </>
  );

  return (
    <>
      {isAnonymous && (
        <div className={className} style={style}>
          {content}
        </div>
      )}
      {!isAnonymous && (
        <InteractiveElement className={className} style={style} onClick={openBooking} arialLabel={t('booking.open')}>
          {content}
        </InteractiveElement>
      )}
    </>
  );
};
export default BookingCard;
